import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { DateInputField } from '../components/DateInputField';
import { authApi } from '../services/api';
import { UpdateProfileRequest } from '../types/auth';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage, SUPPORTED_LANGUAGES } from '../i18n/languages';
import { enumLabel, statusLabel } from '../i18n/translate';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Profile form state
  const [profileData, setProfileData] = useState<UpdateProfileRequest>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phoneNumber: user?.phoneNumber || '',
    dateOfBirth: '',
    driverLicenseNumber: '',
    licenseExpiryDate: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    emailNotifications: true,
    smsNotifications: false,
    preferredLanguage: normalizeLanguage(user?.preferredLanguage || 'en'),
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleProfileChange = (field: keyof UpdateProfileRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setProfileData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPasswordData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      console.log('Updating profile with data:', profileData);
      console.log('User ID:', user.id);
      await authApi.updateProfile(user.id, profileData);
      setSnackbar({
        open: true,
        message: t('profile.messages.updated'),
        severity: 'success',
      });
      // Reload the page to fetch updated user data
      window.location.reload();
    } catch (error: any) {
      console.error('Profile update error:', error);
      console.error('Error response:', error.response);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.message || t('profile.messages.updateFailed'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbar({
        open: true,
        message: t('profile.messages.passwordMismatch'),
        severity: 'error',
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setSnackbar({
        open: true,
        message: t('profile.passwordLength'),
        severity: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      await authApi.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setSnackbar({
        open: true,
        message: t('profile.messages.passwordChanged'),
        severity: 'success',
      });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('profile.messages.passwordChangeFailed'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{t('profile.loginRequired')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            {t('profile.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Chip
              label={enumLabel(t, 'roles', user.role)}
              color="primary"
              size="small"
            />
            <Chip
              label={statusLabel(t, user.status)}
              color={user.status === 'ACTIVE' ? 'success' : 'default'}
              size="small"
            />
            {user.emailVerified && (
              <Chip
                label={t('profile.emailVerified')}
                color="success"
                size="small"
                variant="outlined"
              />
            )}
            {user.phoneVerified && (
              <Chip
                label={t('profile.phoneVerified')}
                color="success"
                size="small"
                variant="outlined"
              />
            )}
            {user.company && (
              <Chip
                label={user.company.name}
                color="secondary"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Tabs value={tabValue} onChange={handleTabChange} aria-label={t('profile.tabsLabel')}>
          <Tab label={t('profile.profileInformation')} />
          <Tab label={t('profile.securityPassword')} />
          <Tab label={t('profile.preferences')} />
        </Tabs>

        {/* Profile Information Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box component="form" onSubmit={handleUpdateProfile}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.firstName')}
                  value={profileData.firstName}
                  onChange={handleProfileChange('firstName')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.lastName')}
                  value={profileData.lastName}
                  onChange={handleProfileChange('lastName')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.username', 'Username')}
                  value={user.username}
                  disabled
                  helperText={t('fields.usernameCannotChange', 'Username cannot be changed')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.phoneNumber')}
                  value={profileData.phoneNumber}
                  onChange={handleProfileChange('phoneNumber')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateInputField
                  fullWidth
                  label={t('fields.dateOfBirth')}
                  value={profileData.dateOfBirth}
                  onChange={(dateOfBirth) => setProfileData({ ...profileData, dateOfBirth })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.driverLicenseNumber')}
                  value={profileData.driverLicenseNumber}
                  onChange={handleProfileChange('driverLicenseNumber')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateInputField
                  fullWidth
                  label={t('fields.licenseExpiryDate')}
                  value={profileData.licenseExpiryDate}
                  onChange={(licenseExpiryDate) =>
                    setProfileData({ ...profileData, licenseExpiryDate })
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {t('profile.addressInformation')}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('fields.streetAddress')}
                  value={profileData.streetAddress}
                  onChange={handleProfileChange('streetAddress')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.city')}
                  value={profileData.city}
                  onChange={handleProfileChange('city')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.state')}
                  value={profileData.state}
                  onChange={handleProfileChange('state')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.postalCode')}
                  value={profileData.postalCode}
                  onChange={handleProfileChange('postalCode')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('fields.country')}
                  value={profileData.country}
                  onChange={handleProfileChange('country')}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading ? t('profile.updating') : t('profile.updateProfile')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Security & Password Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box component="form" onSubmit={handleChangePassword}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('profile.passwordLength')}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label={t('fields.currentPassword')}
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange('currentPassword')}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label={t('fields.newPassword')}
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange('newPassword')}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label={t('fields.confirmNewPassword')}
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange('confirmPassword')}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={loading}
                >
                  {loading ? t('profile.changing') : t('profile.changePassword')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Preferences Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box component="form" onSubmit={handleUpdateProfile}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t('profile.notifications')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={profileData.emailNotifications}
                      onChange={handleProfileChange('emailNotifications')}
                    />
                  }
                  label={t('profile.emailNotifications')}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={profileData.smsNotifications}
                      onChange={handleProfileChange('smsNotifications')}
                    />
                  }
                  label={t('profile.smsNotifications')}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {t('profile.language')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label={t('profile.preferredLanguage')}
                  value={profileData.preferredLanguage}
                  onChange={handleProfileChange('preferredLanguage')}
                  SelectProps={{
                    native: true,
                  }}
                >
                  {SUPPORTED_LANGUAGES.map((language) => (
                    <option key={language.code} value={language.code}>
                      {language.nativeLabel}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading ? t('profile.updating') : t('profile.savePreferences')}
                </Button>
              </Grid>
            </Grid>
    </Box>
        </TabPanel>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
